'use client'

import { CollectionContainer } from '@/components/common/collection/collection-container'
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { useIsMobileClient } from '@/hooks/common/use-device-type'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useEffect, useState } from 'react'

interface OutfitHighlight {
  image: string
  title: string
  description: string
}

export const EccOutfitHighlights = () => {
  const t = useTranslations()
  const isMobile = useIsMobileClient()
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null)

  const outfitHighlights: OutfitHighlight[] = [
    {
      image: '/image/November2025/mceclip7_72.jpg',
      title: t('ecc_collection_hardcode.outfit_highlights.work.title'),
      description: t(
        'ecc_collection_hardcode.outfit_highlights.work.description'
      ),
    },
    {
      image: '/image/November2025/mceclip6_98.jpg',
      title: t('ecc_collection_hardcode.outfit_highlights.travel.title'),
      description: t(
        'ecc_collection_hardcode.outfit_highlights.travel.description'
      ),
    },
    {
      image: '/image/November2025/mceclip4.jpg',
      title: t('ecc_collection_hardcode.outfit_highlights.coffee.title'),
      description: t(
        'ecc_collection_hardcode.outfit_highlights.coffee.description'
      ),
    },
    {
      image: '/image/November2025/mceclip5_4.jpg',
      title: t('ecc_collection_hardcode.outfit_highlights.sport.title'),
      description: t(
        'ecc_collection_hardcode.outfit_highlights.sport.description'
      ),
    },
  ]

  // Auto play effect
  useEffect(() => {
    if (!carouselApi || outfitHighlights.length < 2) return
    const interval = setInterval(() => {
      if (carouselApi) {
        carouselApi.scrollNext()
      }
    }, 3000) // 3 seconds
    return () => clearInterval(interval)
  }, [carouselApi, outfitHighlights.length])

  return (
    <CollectionContainer>
      <div className='mb-4 text-center text-xl font-medium uppercase md:mb-6 md:text-[28px]'>
        {t('ecc_collection_hardcode.outfit_highlights.title')}
      </div>
      <Carousel
        opts={{
          align: 'start',
          loop: true,
          skipSnaps: false,
          slidesToScroll: 1,
        }}
        setApi={setCarouselApi}
        className='w-full'
      >
        <CarouselContent className='-ml-4'>
          {outfitHighlights.map((outfitHighlight, index) => (
            <CarouselItem
              key={index}
              className={cn(
                'group cursor-pointer pl-4',
                isMobile ? 'basis-[66.666%]' : 'basis-1/4'
              )}
            >
              <div className='relative overflow-hidden rounded-lg'>
                <div className='aspect-[434/581]'>
                  <Image
                    src={ImageHelper.getImageUrlCdn(outfitHighlight.image)}
                    alt={outfitHighlight.title}
                    fill
                    className='h-full w-full object-cover transition-all duration-500 ease-in-out group-hover:scale-105'
                  />
                </div>
                <div className='absolute bottom-0 left-0 flex h-1/2 w-full flex-col justify-end bg-gradient-to-t from-neutral-800 to-transparent p-4 pb-3 text-white md:pb-6'>
                  <h3 className='text-lg font-medium uppercase text-white 2xl:text-2xl'>
                    {outfitHighlight.title}
                  </h3>
                  <p className='text-xs text-white opacity-90 md:text-base'>
                    {outfitHighlight.description}
                  </p>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>

        <CarouselNavigation />
      </Carousel>
    </CollectionContainer>
  )
}

function CarouselNavigation() {
  return (
    <>
      <CarouselPrevious
        className='absolute -left-3 top-1/2 h-auto w-auto -translate-y-1/2 rounded-full bg-black p-2 text-white md:-left-5 md:p-3'
        variant='outline'
        hideIfStart
      />
      <CarouselNext
        className='absolute -right-3 top-1/2 h-auto w-auto -translate-y-1/2 rounded-full bg-black p-2 text-white md:-right-5 md:p-3'
        variant='outline'
        hideIfEnd
      />
    </>
  )
}
